import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { useDepos } from '../lib/useDepos';
import { useFirms } from '../lib/useFirms';
import { useSubcoCompanies } from '../lib/useSubcoCompanies';
import { SubcoTagCell, TagPicker } from '../components/subco/SubcoTagCell';
import { formatDate, getStatusColor } from '../lib/utils';
import { turkishIncludes, normalizeTurkish } from '../lib/turkishSearch';
import { formatLastSeenTr } from '../lib/relativeTime';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Users as UsersIcon,
  Clock,
  UserCheck,
  UserX,
  AlertTriangle,
  Calendar,
  Eye,
  Trash2,
  Building2,
  MoreHorizontal,
  UserCog,
  Ban,
  RefreshCw,
  Filter,
  Download,
  Upload,
  FileJson,
  LogIn,
  Mail,
  KeyRound,
  UserPlus,
  Shield,
  ShieldCheck,
  Lock
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Checkbox } from '../components/ui/checkbox';

export const Users = () => {
  const { t, language } = useTranslation();
  const { depos: dbDepos } = useDepos();
  // Firmalar — cross-tab senkron hook (Haz 13, 2026). Firms sayfasında yeni
  // firma eklenince burada (ve diğer sekmelerde) anında listelenir.
  const { firms } = useFirms();
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Şub 2026 — Sayfa yetkisi kim erişebiliyor lookup
  const [permLookupOpen, setPermLookupOpen] = useState(false);
  const [permLookupKey, setPermLookupKey] = useState('planning-copy');
  const [permLookupData, setPermLookupData] = useState(null);
  const [permLookupLoading, setPermLookupLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('status')?.toUpperCase() || 'ACTIVE');
  const [selectedFirmFilter, setSelectedFirmFilter] = useState('all');
  const [selectedDepoFilter, setSelectedDepoFilter] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [passwordResetDialog, setPasswordResetDialog] = useState({ open: false, user: null, sending: false });
  const [setPasswordDialog, setSetPasswordDialog] = useState({ open: false, user: null, password: '', saving: false });
  // Şub 28, 2026 — SUPER_ADMIN kullanıcı email düzeltme
  const [emailDialog, setEmailDialog] = useState({ open: false, user: null, newEmail: '', saving: false });
  
  // Bulk selection states
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState({ open: false });
  
  // Dialog states
  const [approveDialog, setApproveDialog] = useState({ open: false, user: null, step: 1 });
  const [rejectDialog, setRejectDialog] = useState({ open: false, user: null, step: 1 });
  const [statusDialog, setStatusDialog] = useState({ open: false, user: null, action: null });
  const [firmChangeDialog, setFirmChangeDialog] = useState({ open: false, user: null, newFirmId: '' });
  const [roleChangeDialog, setRoleChangeDialog] = useState({ open: false, user: null, newRole: '' });
  const [importDialog, setImportDialog] = useState({ open: false, data: null, firms: null, results: null });
  
  // Permissions Dialog state
  const [permDialog, setPermDialog] = useState({ open: false, user: null });
  const [permData, setPermData] = useState({
    allowed_firms: [],
    can_delete: false,
    allowed_pages: [],
    can_view_financial: false,
    can_send_notifications: false,
  });
  const [permSaving, setPermSaving] = useState(false);
  
  const PAGE_KEYS = [
    { key: 'daily-status', label: 'Çalışan Durumu' },
    { key: 'work-statistics', label: 'Çalışan İstatistikleri' },
    { key: 'routes', label: 'Routelar – Tüm Routelar', group: 'Routelar' },
    { key: 'routes-reports', label: 'Routelar – Route Raporları', group: 'Routelar' },
    { key: 'routes-drivers', label: 'Routelar – Şoför Analizi', group: 'Routelar' },
    { key: 'routes-fuel', label: 'Routelar – Yakıt Raporu', group: 'Routelar' },
    { key: 'fuel-submissions', label: 'Routelar – Mazot Bildirimleri', group: 'Routelar' },
    { key: 'customers', label: 'Routelar – Müşteriler (tüm firmalar için ortak liste)', group: 'Routelar' },
    { key: 'vehicles', label: 'Araçlar – Araç Listesi', group: 'Araçlar' },
    { key: 'vehicle-overview', label: 'Araçlar – Araç Özeti & Atama (şoför / vergunning atama)', group: 'Araçlar' },
    { key: 'vehicle-reports', label: 'Araçlar – Araç Raporu', group: 'Araçlar' },
    { key: 'users', label: 'Kullanıcı Yönetimi', group: 'Kullanıcılar' },
    { key: 'employees', label: 'Çalışan Profilleri', group: 'Kullanıcılar' },
    { key: 'employee-overview', label: 'Çalışan Evrak Özeti', group: 'Kullanıcılar' },
    { key: 'employee-summary', label: 'Personel Özet', group: 'Kullanıcılar' },
    { key: 'job-applications', label: 'İş Başvuruları', group: 'Kullanıcılar' },
    { key: 'leave', label: 'İzin Yönetimi' },
    { key: 'employee-tracking', label: 'Çalışan Takibi' },
    { key: 'documents', label: 'Doküman Yönetimi' },
    { key: 'loonbrief', label: 'Bordro Yükleme' },
    { key: 'loonbrief-list', label: 'Bordro Listesi' },
    // Planlama menüsü (Şub 09, 2026 — granüler yetkilendirme)
    // Haz 13, 2026 — 'planning-edit' eklendi: 'planning' SADECE okuma izni,
    // 'planning-edit' atama/düzenleme/silme yetkisi.
    { key: 'planning', label: 'Planlama – Görüntüle (sadece okuma)', group: 'Planlama' },
    { key: 'planning-edit', label: 'Planlama – Atama / Düzenleme / Silme', group: 'Planlama' },
    { key: 'planning-list', label: 'Planlama – Planlama Listele', group: 'Planlama' },
    { key: 'planning-forms', label: 'Planlama – Form Doldurmaları (kim doldurdu)', group: 'Planlama' },
    { key: 'planning-start-stop', label: 'Planlama – Start-Stop Kayıtları', group: 'Planlama' },
    { key: 'planning-summary', label: 'Planlama – Özet', group: 'Planlama' },
    { key: 'planning-board', label: 'Planlama – Board', group: 'Planlama' },
    // Şub 2026 — "Önceki Haftadan Kopyala" işlemi için ayrı yetki
    { key: 'planning-copy', label: 'Planlama – Hafta Kopyalama (Önceki Haftadan Kopyala)', group: 'Planlama' },
    // Şub 16, 2026 — PostNL menü sayfaları (Dagplanning, Ritmonitor, Kodlar)
    { key: 'postnl-dagplanning', label: 'PostNL – Dagplanning', group: 'PostNL' },
    { key: 'postnl-ritmonitor', label: 'PostNL – Ritmonitor', group: 'PostNL' },
    { key: 'postnl-codes', label: 'PostNL – Kodlar', group: 'PostNL' },
    { key: 'postnl-credentials', label: 'PostNL – Hesaplar', group: 'PostNL' },
    { key: 'postnl-distribution', label: 'PostNL – Distributielijsten', group: 'PostNL' },
    // Şub 27, 2026 — DPD alt-sayfa yetkileri (SUPER_ADMIN'in tüm menüsü)
    { key: 'dpd-summary', label: 'DPD Günlük Özet', group: 'DPD' },
    { key: 'dpd-performance', label: 'DPD Şoför Performansı', group: 'DPD' },
    { key: 'dpd-consolidated', label: 'DPD Konsolide Rapor', group: 'DPD' },
    { key: 'dpd-invoice', label: 'DPD Aylık Fatura', group: 'DPD' },
    { key: 'dpd-heatmap', label: 'DPD Retour Isı Haritası', group: 'DPD' },
    { key: 'dpd-subco', label: 'DPD Subco Karşılaştırma', group: 'DPD' },
    { key: 'dpd-couriers', label: 'DPD Kurye Eşleştirme', group: 'DPD' },
    { key: 'dpd-retour', label: 'DPD Retour Raporu', group: 'DPD' },
    { key: 'dpd-files', label: 'DPD Dosya Kategorileri', group: 'DPD' },
    { key: 'dpd-analytics', label: 'DPD Analitik & İçgörü', group: 'DPD' },
    { key: 'dpd-missing', label: 'DPD Eksik Dosyalar', group: 'DPD' },
    // Çalışan profili alt-tab kısıtlamaları (employees yetkisi ek olarak gerekir)
    { key: 'employees-tab-packages', label: 'Çalışan Profili – Paketler tabı', group: 'Çalışan Profili Tabları' },
    { key: 'employees-tab-loonbriefs', label: 'Çalışan Profili – Bordrolar tabı', group: 'Çalışan Profili Tabları' },
    { key: 'employees-tab-finance', label: 'Çalışan Profili – Finansal Veriler tabı', group: 'Çalışan Profili Tabları' },
    { key: 'employees-tab-notes', label: 'Çalışan Profili – Notlar tabı', group: 'Çalışan Profili Tabları' },
    // Tek toggle: admin'in kendi çalışan menüsünü görmesi.
    // Açılırsa normal EMPLOYEE rolünün TÜM sol menüsü (Kontrol Paneli, Çalışma Durumum,
    // Bodrolarım, Paketlerim, Finansal Verilerim, İzin Talepleri, Dosyalarım, İletişim,
    // Bildirimlerim, Profilim) admin sidebar'ına eklenir.
    { key: 'employee-self-view', label: 'Kendi çalışan menümü göster (tam çalışan deneyimi)', group: 'Kişisel Çalışan Menüsü' },
    { key: 'settings', label: 'Ayarlar / Bildirimler' },
  ];

  const openPermDialog = async (u) => {
    setPermDialog({ open: true, user: u });
    try {
      const res = await api.get(`/admin/users/${u.id}/permissions`);
      if (res.data.success && res.data.data) {
        setPermData({
          allowed_firms: res.data.data.allowed_firms || [],
          can_delete: res.data.data.can_delete || false,
          allowed_pages: res.data.data.allowed_pages || [],
          can_view_financial: res.data.data.can_view_financial || false,
          can_send_notifications: res.data.data.can_send_notifications || false,
        });
      } else {
        setPermData({ allowed_firms: [], can_delete: false, allowed_pages: [], can_view_financial: false, can_send_notifications: false });
      }
    } catch { setPermData({ allowed_firms: [], can_delete: false, allowed_pages: [], can_view_financial: false, can_send_notifications: false }); }
  };

  const handleSavePermissions = async () => {
    setPermSaving(true);
    try {
      await api.put(`/admin/users/${permDialog.user.id}/permissions`, permData);
      toast.success(t('common.success'));
      setPermDialog({ open: false, user: null });
      fetchAllUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setPermSaving(false);
    }
  };

  const togglePageKey = (key) => {
    setPermData(prev => ({
      ...prev,
      allowed_pages: prev.allowed_pages.includes(key)
        ? prev.allowed_pages.filter(k => k !== key)
        : [...prev.allowed_pages, key]
    }));
  };

  const toggleFirm = (firmId) => {
    setPermData(prev => ({
      ...prev,
      allowed_firms: prev.allowed_firms.includes(firmId)
        ? prev.allowed_firms.filter(f => f !== firmId)
        : [...prev.allowed_firms, firmId]
    }));
  };
  
  // Create User Dialog state
  const [createUserDialog, setCreateUserDialog] = useState({ 
    open: false, 
    firmId: '', 
    fullName: '', 
    email: '', 
    phone: '', 
    language: 'tr',
    password: '',
    role: 'EMPLOYEE',
    sendNotification: false 
  });
  
  const [selectedFirmId, setSelectedFirmId] = useState('');
  const [selectedRole, setSelectedRole] = useState('EMPLOYEE');
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  // Status counts
  const [statusCounts, setStatusCounts] = useState({
    PENDING: 0,
    ACTIVE: 0,
    REJECTED: 0,
    SUSPENDED: 0
  });

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isFirmAdmin = user?.role === 'FIRM_ADMIN';
  const canFilterFirm = user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN';
  const { subcos, tags: userTags, setTags: setUserTags } = useSubcoCompanies(canFilterFirm);
  const createSubco = subcos.find((s) => s.id === createUserDialog.subcoId);
  const createFirmOpts = createSubco?.firm_ids?.length ? firms.filter((f) => createSubco.firm_ids.includes(f.id)) : firms;

  useEffect(() => {
    fetchAllUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select tek firma varsa (useFirms() yüklendikten sonra)
  useEffect(() => {
    if (firms.length === 1 && !selectedFirmId) {
      setSelectedFirmId(firms[0].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firms]);

  // Live presence: listen to WS online_status events so the "Son Görülme" column
  // reflects changes in real time without requiring a full refresh. Also auto
  // re-render every minute so "5 dk önce" labels advance naturally.
  const [, setPresenceTick] = useState(0);
  useEffect(() => {
    const handleOnlineStatus = (e) => {
      const onlineList = e.detail || [];
      const nowIso = new Date().toISOString();
      setAllUsers(prev => prev.map(u => {
        const wasOnline = u.is_online;
        const nowOnline = onlineList.includes(u.id);
        // If user just went offline, stamp last_seen locally.
        const last_seen = (wasOnline && !nowOnline) ? nowIso : u.last_seen;
        return { ...u, is_online: nowOnline, last_seen };
      }));
    };
    window.addEventListener('ws:online_status', handleOnlineStatus);
    // Seed from current window state if available.
    if (Array.isArray(window.__onlineUsers) && window.__onlineUsers.length > 0) {
      handleOnlineStatus({ detail: window.__onlineUsers });
    }
    const tick = setInterval(() => setPresenceTick(n => n + 1), 60000);
    return () => {
      window.removeEventListener('ws:online_status', handleOnlineStatus);
      clearInterval(tick);
    };
  }, []);

  useEffect(() => {
    setSearchParams({ status: activeTab });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const [pendingRes, activeRes, rejectedRes, suspendedRes] = await Promise.all([
        api.get('/admin/users?status=PENDING'),
        api.get('/admin/users?status=ACTIVE'),
        api.get('/admin/users?status=REJECTED'),
        api.get('/admin/users?status=SUSPENDED')
      ]);
      
      const pending = pendingRes.data.success ? pendingRes.data.data : [];
      const active = activeRes.data.success ? activeRes.data.data : [];
      const rejected = rejectedRes.data.success ? rejectedRes.data.data : [];
      const suspended = suspendedRes.data.success ? suspendedRes.data.data : [];
      
      const all = [...pending, ...active, ...rejected, ...suspended];
      setAllUsers(all);
      
      setStatusCounts({
        PENDING: pending.length,
        ACTIVE: active.length,
        REJECTED: rejected.length,
        SUSPENDED: suspended.length
      });
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // fetchFirms artık `useFirms()` hook'una taşındı (cross-tab cache).

  // Filtered users based on tab, firm, and search
  const filteredUsers = useMemo(() => {
    return allUsers.filter(u => {
      // Status filter
      if (activeTab === 'PENDING' && u.status !== 'PENDING') return false;
      if (activeTab === 'ACTIVE' && u.status !== 'ACTIVE') return false;
      if (activeTab === 'REJECTED' && u.status !== 'REJECTED') return false;
      if (activeTab === 'SUSPENDED' && u.status !== 'SUSPENDED') return false;
      
      // Firm filter
      if (selectedFirmFilter !== 'all' && u.firm_id !== selectedFirmFilter) return false;
      
      // Depo filter
      if (selectedDepoFilter !== 'all') {
        if (selectedDepoFilter === '__none__') {
          if (u.depo) return false;
        } else {
          if (u.depo !== selectedDepoFilter) return false;
        }
      }
      
      // Search filter
      if (searchTerm) {
        const matchName = turkishIncludes(u.full_name || '', searchTerm);
        const matchEmail = turkishIncludes(u.email || '', searchTerm);
        const matchPhone = u.gsm?.includes(searchTerm);
        if (!matchName && !matchEmail && !matchPhone) return false;
      }
      
      return true;
    });
  }, [allUsers, activeTab, selectedFirmFilter, selectedDepoFilter, searchTerm]);

  // Filtered counts based on firm filter
  const filteredCounts = useMemo(() => {
    const counts = { PENDING: 0, ACTIVE: 0, REJECTED: 0, SUSPENDED: 0 };
    allUsers.forEach(u => {
      if (selectedFirmFilter !== 'all' && u.firm_id !== selectedFirmFilter) return;
      if (selectedDepoFilter !== 'all') {
        if (selectedDepoFilter === '__none__' && u.depo) return;
        if (selectedDepoFilter !== '__none__' && u.depo !== selectedDepoFilter) return;
      }
      if (u.status === 'PENDING') counts.PENDING++;
      else if (u.status === 'ACTIVE') counts.ACTIVE++;
      else if (u.status === 'REJECTED') counts.REJECTED++;
      else if (u.status === 'SUSPENDED') counts.SUSPENDED++;
    });
    return counts;
  }, [allUsers, selectedFirmFilter, selectedDepoFilter]);

  const openApproveDialog = (targetUser) => {
    setApproveDialog({ open: true, user: targetUser, step: 1 });
    setSelectedRole('EMPLOYEE');
    if (targetUser.firm_id) {
      // Kullanıcının zaten firma ataması varsa (domain üzerinden üye olmuş)
      setSelectedFirmId(targetUser.firm_id);
    } else if (isFirmAdmin && user?.firm_id) {
      setSelectedFirmId(user.firm_id);
    } else if (firms.length === 1) {
      setSelectedFirmId(firms[0].id);
    } else {
      setSelectedFirmId('');
    }
  };

  const openRejectDialog = (targetUser) => {
    setRejectDialog({ open: true, user: targetUser, step: 1 });
    setRejectReason('');
  };

  const openStatusDialog = (targetUser, action) => {
    setStatusDialog({ open: true, user: targetUser, action });
  };

  const openDeleteDialog = (targetUser) => {
    setDeleteDialog({ open: true, user: targetUser });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, user: null });
  };

  const closeApproveDialog = () => {
    setApproveDialog({ open: false, user: null, step: 1 });
    setSelectedFirmId('');
    setSelectedRole('EMPLOYEE');
  };

  const closeRejectDialog = () => {
    setRejectDialog({ open: false, user: null, step: 1 });
    setRejectReason('');
  };

  const closeStatusDialog = () => {
    setStatusDialog({ open: false, user: null, action: null });
  };

  const handleImpersonate = async (targetUser) => {
    try {
      const response = await api.post(`/admin/users/${targetUser.id}/impersonate`);
      if (response.data.success) {
        const { token, user_email, user_name } = response.data.data;
        
        // Save current admin session before clearing
        const currentAuth = localStorage.getItem('hr-auth-storage');
        if (currentAuth) {
          sessionStorage.setItem('admin-original-session', currentAuth);
          sessionStorage.setItem('admin-original-name', user?.full_name || user?.email || 'Admin');
          sessionStorage.setItem('admin-original-role', user?.role || 'SUPER_ADMIN');
        }
        
        // Clear current auth and set new token
        localStorage.clear();
        localStorage.setItem('impersonate_token', token);
        
        // Redirect to login page with impersonate token (same window)
        toast.success(`${user_name || user_email} olarak giriş yapılıyor...`);
        
        // Use same window - full page reload to clear all state
        window.location.href = `/login?impersonate=${token}`;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Giriş yapılamadı');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;
    setProcessing(true);
    try {
      const response = await api.delete(`/admin/users/${deleteDialog.user.id}`);
      if (response.data.success) {
        toast.success(t('admin.userDeleted') || 'Kullanıcı silindi');
        closeDeleteDialog();
        fetchAllUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  // Bulk selection handlers
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllSelection = () => {
    const selectableUsers = filteredUsers.filter(u => u.role !== 'SUPER_ADMIN');
    if (selectedUsers.length === selectableUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(selectableUsers.map(u => u.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) return;
    setProcessing(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const userId of selectedUsers) {
        try {
          await api.delete(`/admin/users/${userId}`);
          successCount++;
        } catch (error) {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} kullanıcı silindi`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} kullanıcı silinemedi`);
      }
      
      setSelectedUsers([]);
      setBulkDeleteDialog({ open: false });
      fetchAllUsers();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  // Clear selection when tab changes
  useEffect(() => {
    setSelectedUsers([]);
  }, [activeTab]);

  // Send password reset email to user
  const handleSendPasswordReset = async () => {
    if (!passwordResetDialog.user) return;
    setPasswordResetDialog(prev => ({ ...prev, sending: true }));
    try {
      const response = await api.post(`/admin/users/${passwordResetDialog.user.id}/send-password-reset`);
      if (response.data.success) {
        toast.success(
          (t('admin.passwordResetSent') || 'Şifre sıfırlama linki {email} adresine gönderildi')
            .replace('{email}', passwordResetDialog.user.email)
        );
        setPasswordResetDialog({ open: false, user: null, sending: false });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setPasswordResetDialog(prev => ({ ...prev, sending: false }));
    }
  };

  const handleApproveStep1 = () => {
    if (canFilterFirm && !selectedFirmId) {
      toast.error(t('admin.firmRequired') || 'Firma seçimi zorunludur');
      return;
    }
    setApproveDialog({ ...approveDialog, step: 2 });
  };

  const handleApprove = async () => {
    if (!approveDialog.user) return;
    setProcessing(true);
    
    try {
      const body = { 
        firm_id: selectedFirmId,
        role: selectedRole 
      };
      await api.put(`/admin/users/${approveDialog.user.id}/approve`, body);
      toast.success(t('admin.userApproved') || 'Kullanıcı onaylandı');
      closeApproveDialog();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectStep1 = () => {
    if (rejectReason.trim().length < 5) {
      toast.error(t('admin.reasonRequired') || 'Red sebebi en az 5 karakter olmalı');
      return;
    }
    setRejectDialog({ ...rejectDialog, step: 2 });
  };

  const handleReject = async () => {
    if (!rejectDialog.user) return;
    setProcessing(true);
    
    try {
      await api.put(`/admin/users/${rejectDialog.user.id}/reject`, { reason: rejectReason });
      toast.success(t('admin.userRejected') || 'Kullanıcı reddedildi');
      closeRejectDialog();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleStatusChange = async () => {
    if (!statusDialog.user || !statusDialog.action) return;
    setProcessing(true);
    
    try {
      const newStatus = statusDialog.action === 'activate' ? 'ACTIVE' : 'SUSPENDED';
      await api.put(`/admin/users/${statusDialog.user.id}/status`, { status: newStatus });
      toast.success(
        statusDialog.action === 'activate' 
          ? (t('admin.userActivated') || 'Kullanıcı aktif edildi')
          : (t('admin.userSuspended') || 'Kullanıcı askıya alındı')
      );
      closeStatusDialog();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  // Firma değiştirme
  const openFirmChangeDialog = (userToChange) => {
    setFirmChangeDialog({ 
      open: true, 
      user: userToChange, 
      newFirmId: userToChange.firm_id || '' 
    });
  };

  const closeFirmChangeDialog = () => {
    setFirmChangeDialog({ open: false, user: null, newFirmId: '' });
  };

  const handleFirmChange = async () => {
    if (!firmChangeDialog.user || !firmChangeDialog.newFirmId) return;
    if (firmChangeDialog.newFirmId === firmChangeDialog.user.firm_id) {
      toast.error('Lütfen farklı bir firma seçin');
      return;
    }
    
    setProcessing(true);
    try {
      await api.put(`/admin/users/${firmChangeDialog.user.id}/firm`, { 
        firm_id: firmChangeDialog.newFirmId 
      });
      const newFirm = firms.find(f => f.id === firmChangeDialog.newFirmId);
      toast.success(`Kullanıcı "${newFirm?.name || 'Yeni Firma'}" firmasına aktarıldı`);
      closeFirmChangeDialog();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  // Rol değiştirme
  const openRoleChangeDialog = (userToChange) => {
    setRoleChangeDialog({ 
      open: true, 
      user: userToChange, 
      newRole: userToChange.role || 'EMPLOYEE',
      firmId: userToChange.firm_id || ''
    });
  };

  const closeRoleChangeDialog = () => {
    setRoleChangeDialog({ open: false, user: null, newRole: '', firmId: '' });
  };

  const handleRoleChange = async () => {
    if (!roleChangeDialog.user || !roleChangeDialog.newRole) return;
    if (roleChangeDialog.newRole === roleChangeDialog.user.role) {
      toast.error('Lütfen farklı bir rol seçin');
      return;
    }
    // Require firm when demoting from SUPER_ADMIN to a firm-based role
    const needsFirm = roleChangeDialog.newRole !== 'SUPER_ADMIN' && !roleChangeDialog.user.firm_id;
    if (needsFirm && !roleChangeDialog.firmId) {
      toast.error('Lütfen bir firma seçin');
      return;
    }
    
    setProcessing(true);
    try {
      const payload = { role: roleChangeDialog.newRole };
      if (roleChangeDialog.firmId && roleChangeDialog.newRole !== 'SUPER_ADMIN') {
        payload.firm_id = roleChangeDialog.firmId;
      }
      await api.put(`/admin/users/${roleChangeDialog.user.id}/role`, payload);
      const roleLabels = {
        'SUPER_ADMIN': 'Super Admin',
        'FULL_ADMIN': 'Full Admin',
        'FIRM_ADMIN': 'Firma Admin',
        'EMPLOYEE': 'Çalışan'
      };
      toast.success(`Kullanıcı rolü "${roleLabels[roleChangeDialog.newRole] || roleChangeDialog.newRole}" olarak değiştirildi`);
      closeRoleChangeDialog();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  // Export users to JSON
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/admin/users/export');
      if (response.data.success) {
        const exportData = response.data.data;
        const jsonString = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`${exportData.total_count} kullanıcı export edildi`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setExporting(false);
    }
  };

  // Import users from JSON file
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        // Support both direct array and nested structure
        const users = data.users || data;
        const firms = data.firms || null; // Firma listesini de al
        if (!Array.isArray(users)) {
          toast.error('Geçersiz dosya formatı. JSON dosyası bir kullanıcı dizisi içermelidir.');
          return;
        }
        setImportDialog({ open: true, data: users, firms: firms, results: null });
      } catch (error) {
        toast.error('JSON dosyası okunamadı. Lütfen geçerli bir JSON dosyası seçin.');
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  const handleImport = async (updateExisting = true) => {
    if (!importDialog.data || importDialog.data.length === 0) return;
    
    setImporting(true);
    try {
      const response = await api.post('/admin/users/import', {
        users: importDialog.data,
        firms: importDialog.firms, // Firmaları da gönder
        update_existing: updateExisting,
        default_password: 'Temp123!'
      });
      
      if (response.data.success) {
        setImportDialog(prev => ({ ...prev, results: response.data.data }));
        toast.success(response.data.message);
        fetchAllUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setImporting(false);
    }
  };

  const closeImportDialog = () => {
    setImportDialog({ open: false, data: null, firms: null, results: null });
  };

  // Create User handlers
  const openCreateUserDialog = () => {
    setCreateUserDialog({
      open: true,
      firmId: firms.length === 1 ? firms[0].id : '',
      fullName: '',
      email: '',
      phone: '',
      language: 'tr',
      password: '',
      role: 'EMPLOYEE',
      sendNotification: false,
      subcoId: '',
      tags: [],
    });
  };

  const closeCreateUserDialog = () => {
    setCreateUserDialog({
      open: false,
      firmId: '',
      fullName: '',
      email: '',
      phone: '',
      language: 'tr',
      password: '',
      role: 'EMPLOYEE',
      sendNotification: false
    });
  };

  const handleCreateUser = async () => {
    // Validation
    if (!createUserDialog.firmId) {
      toast.error('Firma seçimi zorunludur');
      return;
    }
    if (!createUserDialog.fullName.trim()) {
      toast.error('Ad Soyad zorunludur');
      return;
    }
    if (!createUserDialog.email.trim()) {
      toast.error('E-posta zorunludur');
      return;
    }
    if (!createUserDialog.password || createUserDialog.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await api.post('/admin/users/create', {
        firm_id: createUserDialog.firmId,
        full_name: createUserDialog.fullName.trim(),
        email: createUserDialog.email.trim().toLowerCase(),
        gsm: createUserDialog.phone.trim() || null,
        language: createUserDialog.language,
        password: createUserDialog.password,
        role: createUserDialog.role,
        send_notification: createUserDialog.sendNotification,
        subco_id: createUserDialog.subcoId || null,
        tags: createUserDialog.tags || [],
      });
      
      if (response.data.success) {
        toast.success(response.data.message || 'Kullanıcı başarıyla oluşturuldu');
        closeCreateUserDialog();
        fetchAllUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  // Generate random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCreateUserDialog(prev => ({ ...prev, password }));
  };

  const getLanguageLabel = (lang) => {
    switch (lang) {
      case 'tr': return 'Türkçe';
      case 'en': return 'English';
      case 'nl': return 'Nederlands';
      case 'fr': return 'Français';
      default: return lang;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200"><Clock className="h-3 w-3 mr-1" />{t('admin.pendingTab') || 'Bekleyen'}</Badge>;
      case 'ACTIVE':
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><UserCheck className="h-3 w-3 mr-1" />{t('admin.activeTab') }</Badge>;
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-700 border-red-200"><UserX className="h-3 w-3 mr-1" />{t('admin.rejectedTab') || 'Reddedilen'}</Badge>;
      case 'SUSPENDED':
        return <Badge className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600"><Ban className="h-3 w-3 mr-1" />{t('admin.suspended') || 'Askıda'}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return <Badge className="bg-purple-100 text-purple-700 border-purple-200">Super Admin</Badge>;
      case 'FULL_ADMIN':
        return <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">Full Admin</Badge>;
      case 'FIRM_ADMIN':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Firma Admin</Badge>;
      case 'EMPLOYEE':
        return <Badge variant="outline">Çalışan</Badge>;
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 
            className="text-2xl font-bold text-slate-900 dark:text-slate-100"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {t('common.users') }
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t('admin.userManagement') || 'Kullanıcı Yönetimi'}</p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Şub 2026 — Sayfa yetkisi lookup */}
          {isSuperAdmin && (
            <Button
              onClick={() => setPermLookupOpen(true)}
              variant="outline"
              className="gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              data-testid="perm-lookup-btn"
            >
              <ShieldCheck className="h-4 w-4" />
              <span className="hidden sm:inline">Yetki Sorgula</span>
            </Button>
          )}
          {/* Create User - Super Admin Only */}
          {isSuperAdmin && (
            <Button
              onClick={openCreateUserDialog}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="create-user-btn"
            >
              <UserPlus className="h-4 w-4" />
              <span className="hidden sm:inline">{t('admin.createUser') || 'Kullanıcı Ekle'}</span>
            </Button>
          )}
          
          {/* Export/Import - Super Admin Only */}
          {isSuperAdmin && (
            <>
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={exporting}
                className="gap-2"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">{t('admin.export') || 'Export'}</span>
              </Button>
              
              <label>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  className="gap-2 cursor-pointer"
                  asChild
                >
                  <span>
                    <Upload className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('admin.import') || 'Import'}</span>
                  </span>
                </Button>
              </label>
            </>
          )}
          
          <Button
            variant="outline"
            size="icon"
            onClick={fetchAllUsers}
            disabled={loading}
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t('admin.searchUsers') || 'Ad, e-posta veya telefon ile ara...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Firm Filter */}
            {isSuperAdmin && (
              <Select value={selectedFirmFilter} onValueChange={setSelectedFirmFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder={t('admin.allFirms') } />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.allFirms') }</SelectItem>
                  {firms.map(firm => (
                    <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Depo Filter */}
            <Select value={selectedDepoFilter} onValueChange={setSelectedDepoFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="users-depo-filter">
                <SelectValue placeholder={t('common.allDepos')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.allDepos')}</SelectItem>
                <SelectItem value="__none__">{t('common.noDepo')}</SelectItem>
                {dbDepos.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Bulk Actions Bar */}
          {selectedUsers.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-blue-700">
                <CheckCircle className="h-4 w-4" />
                <span className="font-medium">{selectedUsers.length} kullanıcı seçildi</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedUsers([])}
                  className="text-slate-600 dark:text-slate-300"
                >
                  Seçimi Temizle
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteDialog({ open: true })}
                  className="gap-1"
                >
                  <Trash2 className="h-4 w-4" />
                  Seçilenleri Sil
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs with Counts */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full lg:w-auto">
          <TabsTrigger 
            value="ACTIVE" 
            className="gap-2"
            data-testid="tab-active"
          >
            <UserCheck className="h-4 w-4" />
            <span className="hidden sm:inline">{t('admin.activeTab') }</span>
            <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5">
              {filteredCounts.ACTIVE}
            </Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="PENDING" 
            className="gap-2"
            data-testid="tab-pending"
          >
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">{t('admin.pendingTab') || 'Bekleyen'}</span>
            <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5">
              {filteredCounts.PENDING}
            </Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="REJECTED" 
            className="gap-2"
            data-testid="tab-rejected"
          >
            <UserX className="h-4 w-4" />
            <span className="hidden sm:inline">{t('admin.rejectedTab') || 'Reddedilen'}</span>
            <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5">
              {filteredCounts.REJECTED}
            </Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="SUSPENDED" 
            className="gap-2"
            data-testid="tab-suspended"
          >
            <Ban className="h-4 w-4" />
            <span className="hidden sm:inline">{t('admin.suspended') || 'Askıda'}</span>
            <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5">
              {filteredCounts.SUSPENDED}
            </Badge>
          </TabsTrigger>
        </TabsList>

        {/* Tab Contents */}
        {['PENDING', 'ACTIVE', 'REJECTED', 'SUSPENDED'].map(status => (
          <TabsContent key={status} value={status} className="mt-6">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                    <UsersIcon className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>{t('admin.noUsersFound') }</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[40px]">
                            <Checkbox
                              checked={selectedUsers.length === filteredUsers.filter(u => u.role !== 'SUPER_ADMIN').length && filteredUsers.length > 0}
                              onCheckedChange={toggleAllSelection}
                              aria-label="Tümünü seç"
                            />
                          </TableHead>
                          <TableHead>{t('admin.fullName') }</TableHead>
                          <TableHead>{t('admin.email') }</TableHead>
                          <TableHead className="hidden md:table-cell">{t('admin.phone') || 'Telefon'}</TableHead>
                          <TableHead className="hidden md:table-cell">Depo</TableHead>
                          {canFilterFirm && <TableHead className="hidden lg:table-cell">{t('common.firm') }</TableHead>}
                          <TableHead>{t('admin.role') || 'Rol'}</TableHead>
                          <TableHead>{t('admin.status') }</TableHead>
                          <TableHead className="hidden md:table-cell">Son Görülme</TableHead>
                          <TableHead className="text-right">{t('common.actions') || 'İşlemler'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers.map((u) => (
                          <TableRow key={u.id} className={selectedUsers.includes(u.id) ? 'bg-blue-50' : ''}>
                            <TableCell>
                              <Checkbox
                                checked={selectedUsers.includes(u.id)}
                                onCheckedChange={() => toggleUserSelection(u.id)}
                                disabled={u.role === 'SUPER_ADMIN'}
                                aria-label={`${u.full_name} seç`}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{u.full_name}</div>
                              <div className="text-xs text-slate-400 md:hidden">{u.email}</div>
                              <SubcoTagCell u={u} subcos={subcos} tags={userTags} onTagsChanged={setUserTags} canEdit={canFilterFirm}
                                onUpdated={(nu) => setAllUsers((prev) => prev.map((x) => (x.id === nu.id ? { ...x, ...nu } : x)))} />
                            </TableCell>
                            <TableCell className="hidden md:table-cell">{u.email}</TableCell>
                            <TableCell className="hidden md:table-cell">{u.gsm || '-'}</TableCell>
                            <TableCell className="hidden md:table-cell">
                              <Select
                                value={u.depo || "__none__"}
                                onValueChange={async (val) => {
                                  try {
                                    const newDepo = val === "__none__" ? null : val;
                                    await api.put(`/admin/users/${u.id}/depo`, { depo: newDepo });
                                    setAllUsers(prev => prev.map(usr => usr.id === u.id ? { ...usr, depo: newDepo } : usr));
                                    toast.success(`${u.full_name} depo güncellendi`);
                                  } catch (err) {
                                    toast.error('Depo güncellenemedi');
                                  }
                                }}
                              >
                                <SelectTrigger className="h-7 w-[110px] text-xs" data-testid={`depo-select-${u.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">—</SelectItem>
                                  {dbDepos.map(d => (
                                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            {canFilterFirm && (
                              <TableCell className="hidden lg:table-cell">
                                {u.role === 'SUPER_ADMIN' ? (
                                  <span className="text-amber-600 font-medium">Bağımsız</span>
                                ) : (
                                  u.firm_name || <span className="text-slate-400">-</span>
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                {getRoleBadge(u.role)}
                                {(() => {
                                  // Çalışan-admin rozeti: admin rolündeyse ve "kendi çalışan menüsünü göster" toggle açıksa "🪪"
                                  if (u.role !== 'FULL_ADMIN' && u.role !== 'FIRM_ADMIN') return null;
                                  const pages = u?.permissions?.allowed_pages || [];
                                  if (!pages.includes('employee-self-view')) return null;
                                  return (
                                    <span
                                      className="inline-flex items-center justify-center h-5 w-5 rounded-full text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-800/60"
                                      title="Çalışan-admin: kendi çalışan menüsü açık"
                                      data-testid={`user-personal-menu-badge-${u.id}`}
                                    >
                                      🪪
                                    </span>
                                  );
                                })()}
                              </div>
                            </TableCell>
                            <TableCell>{getStatusBadge(u.status)}</TableCell>
                            <TableCell className="hidden md:table-cell">
                              {u.is_online ? (
                                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                                  <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                  </span>
                                  Çevrimiçi
                                </span>
                              ) : u.last_seen ? (
                                <span className="text-xs text-slate-500" title={new Date(u.last_seen).toLocaleString('tr-TR')}>
                                  {formatLastSeenTr(u.last_seen)}
                                </span>
                              ) : u.last_login_at ? (
                                <span className="text-xs text-slate-400" title={`Son giriş: ${new Date(u.last_login_at).toLocaleString('tr-TR')}`}>
                                  {formatLastSeenTr(u.last_login_at)}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-300">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                {/* Quick Action Buttons for PENDING users */}
                                {u.status === 'PENDING' && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openApproveDialog(u)}
                                      className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                      title={t('common.approve') || 'Onayla'}
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openRejectDialog(u)}
                                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      title={t('common.reject') || 'Reddet'}
                                    >
                                      <XCircle className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                
                                {/* Quick Re-approve for REJECTED users */}
                                {u.status === 'REJECTED' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openApproveDialog(u)}
                                    className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                    title={t('admin.reapprove') || 'Tekrar Onayla'}
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                )}
                                
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                  {/* View Details */}
                                  <DropdownMenuItem onClick={() => navigate(`/dashboard/users/${u.id}/balances`)}>
                                    <Eye className="h-4 w-4 mr-2" />
                                    {t('admin.viewDetails') || 'Detaylar'}
                                  </DropdownMenuItem>
                                  
                                  {/* Impersonate - Super Admin Only, not for Super Admins */}
                                  {isSuperAdmin && u.role !== 'SUPER_ADMIN' && u.status === 'ACTIVE' && (
                                    <DropdownMenuItem onClick={() => handleImpersonate(u)} className="text-cyan-600">
                                      <LogIn className="h-4 w-4 mr-2" />
                                      {t('admin.impersonate') || 'Panele Gir'}
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {/* Send Password Reset Email - For Active Users */}
                                  {u.status === 'ACTIVE' && u.role !== 'SUPER_ADMIN' && (
                                    <DropdownMenuItem 
                                      onClick={() => setPasswordResetDialog({ open: true, user: u, sending: false })} 
                                      className="text-orange-600"
                                    >
                                      <KeyRound className="h-4 w-4 mr-2" />
                                      {t('admin.sendPasswordReset') || 'Şifre Sıfırlama Gönder'}
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {/* Set Password Directly */}
                                  {u.email !== 'admin@hrportal.be' && u.id !== user?.id && (
                                    <DropdownMenuItem 
                                      onClick={() => setSetPasswordDialog({ open: true, user: u, password: '', saving: false })}
                                      className="text-amber-600"
                                      data-testid={`set-password-btn-${u.id}`}
                                    >
                                      <Lock className="h-4 w-4 mr-2" />
                                      {t('admin.setPassword') || 'Şifre Belirle'}
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {/* Change Firm - Super Admin Only */}
                                  {isSuperAdmin && u.role !== 'SUPER_ADMIN' && (
                                    <DropdownMenuItem onClick={() => openFirmChangeDialog(u)} className="text-blue-600">
                                      <Building2 className="h-4 w-4 mr-2" />
                                      {t('admin.changeFirm') || 'Firma Değiştir'}
                                    </DropdownMenuItem>
                                  )}

                                  {/* Change Email - Super Admin only, Şub 28, 2026 */}
                                  {isSuperAdmin && u.email !== 'admin@hrportal.be' && u.id !== user?.id && (
                                    <DropdownMenuItem onClick={() => setEmailDialog({ open: true, user: u, newEmail: u.email, saving: false })} className="text-sky-600" data-testid={`change-email-${u.id}`}>
                                      <Mail className="h-4 w-4 mr-2" />
                                      Email'i Düzelt
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {/* Change Role - Super Admin Only, protected admin and self excluded */}
                                  {isSuperAdmin && u.email !== 'admin@hrportal.be' && u.id !== user?.id && (
                                    <DropdownMenuItem onClick={() => openRoleChangeDialog(u)} className="text-purple-600">
                                      <UserCog className="h-4 w-4 mr-2" />
                                      {t('admin.changeRole') || 'Rol Değiştir'}
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {/* Page Permissions - Super Admin Only, for FULL_ADMIN and FIRM_ADMIN */}
                                  {isSuperAdmin && (u.role === 'FULL_ADMIN' || u.role === 'FIRM_ADMIN') && u.email !== 'admin@hrportal.be' && (
                                    <DropdownMenuItem onClick={() => openPermDialog(u)} className="text-indigo-600" data-testid={`perm-btn-${u.id}`}>
                                      <Shield className="h-4 w-4 mr-2" />
                                      {t('admin.pagePermissions') || 'Sayfa Yetkisi'}
                                    </DropdownMenuItem>
                                  )}
                                  
                                  <DropdownMenuSeparator />
                                  
                                  {/* Pending Actions */}
                                  {u.status === 'PENDING' && (
                                    <>
                                      <DropdownMenuItem onClick={() => openApproveDialog(u)} className="text-emerald-600">
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        {t('common.approve') }
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => openRejectDialog(u)} className="text-red-600">
                                        <XCircle className="h-4 w-4 mr-2" />
                                        {t('common.reject') || 'Reddet'}
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  
                                  {/* Active User Actions */}
                                  {u.status === 'ACTIVE' && u.role !== 'SUPER_ADMIN' && (
                                    <DropdownMenuItem onClick={() => openStatusDialog(u, 'suspend')} className="text-amber-600">
                                      <Ban className="h-4 w-4 mr-2" />
                                      {t('admin.suspendUser') || 'Askıya Al'}
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {/* Suspended User Actions */}
                                  {u.status === 'SUSPENDED' && (
                                    <DropdownMenuItem onClick={() => openStatusDialog(u, 'activate')} className="text-emerald-600">
                                      <UserCheck className="h-4 w-4 mr-2" />
                                      {t('admin.activateUser') || 'Aktif Et'}
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {/* Rejected User Actions */}
                                  {u.status === 'REJECTED' && (
                                    <DropdownMenuItem onClick={() => openApproveDialog(u)} className="text-emerald-600">
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      {t('admin.reapprove') || 'Tekrar Onayla'}
                                    </DropdownMenuItem>
                                  )}
                                  
                                  <DropdownMenuSeparator />
                                  
                                  {/* Delete */}
                                  {u.role !== 'SUPER_ADMIN' && (
                                    <DropdownMenuItem onClick={() => openDeleteDialog(u)} className="text-red-600">
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      {t('common.delete') }
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Approve Dialog */}
      <Dialog open={approveDialog.open} onOpenChange={(v) => !v && closeApproveDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {approveDialog.step === 1 
                ? (t('admin.approveUser') || 'Kullanıcıyı Onayla')
                : (t('admin.confirmApproval') || 'Onayı Doğrula')}
            </DialogTitle>
            <DialogDescription>
              {approveDialog.user?.full_name} ({approveDialog.user?.email})
            </DialogDescription>
          </DialogHeader>
          
          {approveDialog.step === 1 ? (
            <div className="space-y-4">
              {isSuperAdmin && (
                <div>
                  <Label>{t('common.firm') } *</Label>
                  <Select value={selectedFirmId} onValueChange={setSelectedFirmId}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('admin.selectFirm') || 'Firma seçin'} />
                    </SelectTrigger>
                    <SelectContent>
                      {firms.map(firm => (
                        <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label>{t('admin.role') || 'Rol'}</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EMPLOYEE">{t('admin.employee') }</SelectItem>
                    <SelectItem value="FIRM_ADMIN">{t('admin.firmAdmin') || 'Firma Admin'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="py-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">{t('admin.approvalSummary') || 'Onay Özeti'}</span>
                </div>
                <ul className="mt-2 text-sm text-emerald-600 space-y-1">
                  <li>• {t('common.firm') }: {firms.find(f => f.id === selectedFirmId)?.name}</li>
                  <li>• {t('admin.role') || 'Rol'}: {selectedRole === 'EMPLOYEE' ? 'Çalışan' : 'Firma Admin'}</li>
                </ul>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={closeApproveDialog}>
              {t('common.cancel') }
            </Button>
            {approveDialog.step === 1 ? (
              <Button onClick={handleApproveStep1}>
                {t('common.next') }
              </Button>
            ) : (
              <Button onClick={handleApprove} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700">
                {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t('common.approve') }
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(v) => !v && closeRejectDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {rejectDialog.step === 1 
                ? (t('admin.rejectUser') || 'Kullanıcıyı Reddet')
                : (t('admin.confirmRejection') || 'Reddi Doğrula')}
            </DialogTitle>
            <DialogDescription>
              {rejectDialog.user?.full_name} ({rejectDialog.user?.email})
            </DialogDescription>
          </DialogHeader>
          
          {rejectDialog.step === 1 ? (
            <div className="space-y-4">
              <div>
                <Label>{t('admin.rejectReason') || 'Red Sebebi'} *</Label>
                <Textarea
                  placeholder={t('admin.rejectReasonPlaceholder') || 'Red sebebini açıklayın...'}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          ) : (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="h-5 w-5" />
                  <span className="font-medium">{t('admin.rejectionWarning') || 'Dikkat'}</span>
                </div>
                <p className="mt-2 text-sm text-red-600">
                  {t('admin.rejectionMessage') || 'Bu kullanıcı reddedilecek ve sisteme giriş yapamayacak.'}
                </p>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                  <strong>{t('admin.reason') || 'Sebep'}:</strong> {rejectReason}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={closeRejectDialog}>
              {t('common.cancel') }
            </Button>
            {rejectDialog.step === 1 ? (
              <Button onClick={handleRejectStep1} variant="destructive">
                {t('common.next') }
              </Button>
            ) : (
              <Button onClick={handleReject} disabled={processing} variant="destructive">
                {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t('common.reject') || 'Reddet'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      <AlertDialog open={statusDialog.open} onOpenChange={(v) => !v && closeStatusDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusDialog.action === 'activate' 
                ? (t('admin.activateUser') || 'Kullanıcıyı Aktif Et')
                : (t('admin.suspendUser') || 'Kullanıcıyı Askıya Al')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{statusDialog.user?.full_name}</strong> ({statusDialog.user?.email})
              <br /><br />
              {statusDialog.action === 'activate' 
                ? (t('admin.activateConfirm') || 'Bu kullanıcı aktif edilecek ve sisteme giriş yapabilecek.')
                : (t('admin.suspendConfirm') || 'Bu kullanıcı askıya alınacak ve sisteme giriş yapamayacak.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel') }</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStatusChange}
              disabled={processing}
              className={statusDialog.action === 'activate' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {statusDialog.action === 'activate' 
                ? (t('admin.activate') || 'Aktif Et')
                : (t('admin.suspend') || 'Askıya Al')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Firm Change Dialog - Super Admin Only */}
      <Dialog open={firmChangeDialog.open} onOpenChange={(v) => !v && closeFirmChangeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {t('admin.changeFirm') || 'Firma Değiştir'}
            </DialogTitle>
            <DialogDescription>
              <strong>{firmChangeDialog.user?.full_name}</strong> kullanıcısını başka bir firmaya aktarın.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm text-slate-500 dark:text-slate-400">Mevcut Firma</Label>
              <p className="font-medium mt-1">
                {firmChangeDialog.user?.firm_name }
              </p>
            </div>
            
            <div>
              <Label>{t('admin.newFirm') || 'Yeni Firma'}</Label>
              <Select
                value={firmChangeDialog.newFirmId}
                onValueChange={(v) => setFirmChangeDialog(prev => ({ ...prev, newFirmId: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('common.selectFirm')} />
                </SelectTrigger>
                <SelectContent>
                  {firms.map(firm => (
                    <SelectItem 
                      key={firm.id} 
                      value={firm.id}
                      disabled={firm.id === firmChangeDialog.user?.firm_id}
                    >
                      {firm.name}
                      {firm.id === firmChangeDialog.user?.firm_id && ' (Mevcut)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {firmChangeDialog.newFirmId && firmChangeDialog.newFirmId !== firmChangeDialog.user?.firm_id && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                  <div className="text-sm text-amber-700">
                    <strong>{firmChangeDialog.user?.full_name}</strong> kullanıcısı{' '}
                    <strong>"{firms.find(f => f.id === firmChangeDialog.newFirmId)?.name}"</strong> firmasına aktarılacak.
                    Bu işlem kullanıcının izin bakiyelerini etkilemez.
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeFirmChangeDialog}>
              {t('common.cancel') }
            </Button>
            <Button 
              onClick={handleFirmChange}
              disabled={processing || !firmChangeDialog.newFirmId || firmChangeDialog.newFirmId === firmChangeDialog.user?.firm_id}
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('admin.transfer') || 'Aktar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Change Dialog - Super Admin Only */}
      <Dialog open={roleChangeDialog.open} onOpenChange={(v) => !v && closeRoleChangeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              {t('admin.changeRole') || 'Rol Değiştir'}
            </DialogTitle>
            <DialogDescription>
              <strong>{roleChangeDialog.user?.full_name}</strong> kullanıcısının rolünü değiştirin.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm text-slate-500 dark:text-slate-400">Mevcut Rol</Label>
              <p className="font-medium mt-1">
                {roleChangeDialog.user?.role === 'SUPER_ADMIN' ? 'Super Admin' :
                 roleChangeDialog.user?.role === 'FIRM_ADMIN' ? 'Firma Admin' : 
                 roleChangeDialog.user?.role === 'EMPLOYEE' ? 'Çalışan' : 
                 roleChangeDialog.user?.role}
              </p>
            </div>
            
            <div>
              <Label>{t('admin.newRole') || 'Yeni Rol'}</Label>
              <Select
                value={roleChangeDialog.newRole}
                onValueChange={(v) => setRoleChangeDialog(prev => ({ ...prev, newRole: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Rol seçin..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem 
                    value="SUPER_ADMIN"
                    disabled={roleChangeDialog.user?.role === 'SUPER_ADMIN'}
                  >
                    Super Admin
                    {roleChangeDialog.user?.role === 'SUPER_ADMIN' && ' (Mevcut)'}
                  </SelectItem>
                  <SelectItem 
                    value="FULL_ADMIN"
                    disabled={roleChangeDialog.user?.role === 'FULL_ADMIN'}
                  >
                    Full Admin
                    {roleChangeDialog.user?.role === 'FULL_ADMIN' && ' (Mevcut)'}
                  </SelectItem>
                  <SelectItem 
                    value="FIRM_ADMIN"
                    disabled={roleChangeDialog.user?.role === 'FIRM_ADMIN'}
                  >
                    Firma Admin
                    {roleChangeDialog.user?.role === 'FIRM_ADMIN' && ' (Mevcut)'}
                  </SelectItem>
                  <SelectItem 
                    value="EMPLOYEE"
                    disabled={roleChangeDialog.user?.role === 'EMPLOYEE'}
                  >
                    Çalışan
                    {roleChangeDialog.user?.role === 'EMPLOYEE' && ' (Mevcut)'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Firm selector - shown when demoting SUPER_ADMIN or changing to firm-based role without firm */}
            {roleChangeDialog.newRole !== 'SUPER_ADMIN' && !roleChangeDialog.user?.firm_id && (
              <div>
                <Label>{t('common.firm') || 'Firma'} *</Label>
                <Select
                  value={roleChangeDialog.firmId}
                  onValueChange={(v) => setRoleChangeDialog(prev => ({ ...prev, firmId: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={t('common.selectFirm')} />
                  </SelectTrigger>
                  <SelectContent>
                    {firms.map(firm => (
                      <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {roleChangeDialog.newRole && roleChangeDialog.newRole !== roleChangeDialog.user?.role && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <UserCog className="h-5 w-5 text-purple-500 flex-shrink-0" />
                  <div className="text-sm text-purple-700">
                    <strong>{roleChangeDialog.user?.full_name}</strong> kullanıcısının rolü{' '}
                    <strong>"{roleChangeDialog.newRole === 'SUPER_ADMIN' ? 'Super Admin' : roleChangeDialog.newRole === 'FIRM_ADMIN' ? 'Firma Admin' : 'Çalışan'}"</strong> olarak değiştirilecek.
                    {roleChangeDialog.newRole === 'SUPER_ADMIN' && (
                      <span className="block mt-1 text-purple-600">
                        ⚠️ Super Admin, tüm sistem üzerinde tam yetkiye sahip olur. Tüm firmaları ve kullanıcıları yönetebilir.
                      </span>
                    )}
                    {roleChangeDialog.newRole === 'FIRM_ADMIN' && (
                      <span className="block mt-1 text-purple-600">
                        ⚠️ Firma Admin, kendi firmasındaki kullanıcıları ve izin taleplerini yönetebilir.
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeRoleChangeDialog}>
              {t('common.cancel') }
            </Button>
            <Button 
              onClick={handleRoleChange}
              disabled={processing || !roleChangeDialog.newRole || roleChangeDialog.newRole === roleChangeDialog.user?.role}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('admin.changeRoleBtn') || 'Rolü Değiştir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permDialog.open} onOpenChange={(v) => !v && setPermDialog({ open: false, user: null })}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="permissions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-indigo-600" />
              {t('admin.pagePermissions') || 'Sayfa Yetkisi'}
            </DialogTitle>
            <DialogDescription>
              {permDialog.user?.full_name} ({permDialog.user?.role === 'FULL_ADMIN' ? 'Full Admin' : 'Firma Admin'})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-2">
            {/* Firma Seçimi - sadece FULL_ADMIN için */}
            {permDialog.user?.role === 'FULL_ADMIN' && (
              <div className="space-y-2">
                <Label className="font-semibold">{t('admin.allowedFirms') || 'Erişebileceği Firmalar'}</Label>
                <p className="text-xs text-slate-500">{t('admin.allowedFirmsDesc') || 'Boş bırakılırsa hiçbir firmayı göremez'}</p>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                  {firms.map(f => (
                    <label key={f.id} className="flex items-center gap-2 text-sm p-1.5 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={permData.allowed_firms.includes(f.id)}
                        onChange={() => toggleFirm(f.id)}
                        className="rounded border-slate-300"
                      />
                      {f.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Sayfa Yetkileri */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">{t('admin.pageAccess') || 'Sayfa Erişimi'}</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs h-7"
                  onClick={() => setPermData(prev => ({
                    ...prev,
                    allowed_pages: prev.allowed_pages.length === PAGE_KEYS.length ? [] : PAGE_KEYS.map(p => p.key)
                  }))}
                >
                  {permData.allowed_pages.length === PAGE_KEYS.length ? (t('common.deselectAll') || 'Tümünü Kaldır') : (t('common.selectAll') || 'Tümünü Seç')}
                </Button>
              </div>
              <p className="text-xs text-slate-500">{(t('admin.pageAccessDesc') || 'Boş bırakılırsa tüm sayfalara erişebilir')} (Müşteriler, Araç Özeti, Araç Raporu, Personel Özet, PostNL Distributielijsten, DPD Eksik Dosyalar hariç — bunlar ancak işaretlenince açılır)</p>
              <div className="grid grid-cols-2 gap-2 border rounded-lg p-2">
                {(() => {
                  // Grupla: önce 'group' alanı olmayan keyler, sonra her grup başlığı + alt keyleri
                  const ungrouped = PAGE_KEYS.filter((p) => !p.group);
                  const groupNames = [...new Set(PAGE_KEYS.filter((p) => p.group).map((p) => p.group))];
                  const renderCheckbox = (p) => (
                    <label key={p.key} className="flex items-center gap-2 text-sm p-1.5 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={permData.allowed_pages.includes(p.key)}
                        onChange={() => togglePageKey(p.key)}
                        className="rounded border-slate-300"
                        data-testid={`perm-page-${p.key}`}
                      />
                      {p.label}
                    </label>
                  );
                  return (
                    <>
                      {ungrouped.map(renderCheckbox)}
                      {groupNames.map((gname) => (
                        <React.Fragment key={gname}>
                          <div className="col-span-2 mt-2 pt-2 border-t border-slate-200 dark:border-slate-700 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            {gname}
                          </div>
                          {PAGE_KEYS.filter((p) => p.group === gname).map(renderCheckbox)}
                        </React.Fragment>
                      ))}
                    </>
                  );
                })()}
              </div>
            </div>
            
            {/* Toggle Ayarlar */}
            <div className="space-y-3 border-t pt-4">
              <Label className="font-semibold">{t('admin.additionalPermissions') || 'Ek Yetkiler'}</Label>
              
              <label className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                <div>
                  <p className="text-sm font-medium">{t('admin.canDelete') || 'Silme Yetkisi'}</p>
                  <p className="text-xs text-slate-500">{t('admin.canDeleteDesc') || 'Kayıt silme işlemi yapabilir'}</p>
                </div>
                <input
                  type="checkbox"
                  checked={permData.can_delete}
                  onChange={(e) => setPermData(prev => ({ ...prev, can_delete: e.target.checked }))}
                  className="rounded border-slate-300 h-5 w-5"
                />
              </label>
              
              <label className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                <div>
                  <p className="text-sm font-medium">{t('admin.canViewFinancial') || 'Finansal Kayıtları Görme'}</p>
                  <p className="text-xs text-slate-500">{t('admin.canViewFinancialDesc') || 'Finansal kayıt ekleme ve avans bölümünü görebilir'}</p>
                </div>
                <input
                  type="checkbox"
                  checked={permData.can_view_financial}
                  onChange={(e) => setPermData(prev => ({ ...prev, can_view_financial: e.target.checked }))}
                  className="rounded border-slate-300 h-5 w-5"
                />
              </label>
              
              <label className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                <div>
                  <p className="text-sm font-medium">{t('admin.canSendNotifications') || 'Bildirim Gönderme'}</p>
                  <p className="text-xs text-slate-500">{t('admin.canSendNotificationsDesc') || 'Kullanıcılara bildirim gönderebilir'}</p>
                </div>
                <input
                  type="checkbox"
                  checked={permData.can_send_notifications}
                  onChange={(e) => setPermData(prev => ({ ...prev, can_send_notifications: e.target.checked }))}
                  className="rounded border-slate-300 h-5 w-5"
                />
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermDialog({ open: false, user: null })}>
              {t('common.cancel') || 'İptal'}
            </Button>
            <Button onClick={handleSavePermissions} disabled={permSaving} data-testid="save-permissions-btn">
              {permSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
              {t('common.save') || 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialog.open} onOpenChange={(v) => !v && closeImportDialog()}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileJson className="h-5 w-5" />
              {t('admin.importUsers') || 'Kullanıcı İçe Aktar'}
            </DialogTitle>
            <DialogDescription>
              JSON dosyasından kullanıcıları içe aktarın
            </DialogDescription>
          </DialogHeader>
          
          {!importDialog.results ? (
            <>
              {/* Preview */}
              <div className="flex-1 overflow-auto">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg mb-4">
                  <div className="flex gap-4 text-sm text-slate-600 dark:text-slate-300">
                    {importDialog.firms && importDialog.firms.length > 0 && (
                      <span>
                        <Building2 className="h-4 w-4 inline mr-1 text-purple-500" />
                        <strong>{importDialog.firms.length}</strong> firma
                      </span>
                    )}
                    <span>
                      <UsersIcon className="h-4 w-4 inline mr-1 text-blue-500" />
                      <strong>{importDialog.data?.length || 0}</strong> kullanıcı içe aktarılacak
                    </span>
                  </div>
                </div>
                
                {importDialog.data && importDialog.data.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>E-posta</TableHead>
                          <TableHead>Ad Soyad</TableHead>
                          <TableHead>Rol</TableHead>
                          <TableHead>Firma</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importDialog.data.slice(0, 10).map((u, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-sm">{u.email}</TableCell>
                            <TableCell>{u.full_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{u.role}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500 dark:text-slate-400">
                              {u.firm_name || u.firm_id || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {importDialog.data.length > 10 && (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center text-slate-500 dark:text-slate-400">
                              ... ve {importDialog.data.length - 10} kullanıcı daha
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
                
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="text-sm text-emerald-700">
                    <strong>✓ Güvenli Import:</strong> Önce firmalar, sonra kullanıcılar içe aktarılır.
                  </div>
                </div>
                
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <div className="text-sm text-amber-700">
                      <strong>Önemli:</strong>
                      <ul className="mt-1 list-disc list-inside space-y-1">
                        <li>Aynı e-posta adresi varsa mevcut kullanıcı güncellenir</li>
                        <li>Yeni kullanıcılar için varsayılan şifre: <code className="bg-amber-100 px-1 rounded">Temp123!</code></li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={closeImportDialog}>
                  {t('common.cancel') }
                </Button>
                <Button
                  onClick={() => handleImport(true)}
                  disabled={importing || !importDialog.data?.length}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {importing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t('admin.importAndUpdate') || 'İçe Aktar ve Güncelle'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              {/* Results */}
              <div className="flex-1 overflow-auto space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">{importDialog.results.created}</p>
                    <p className="text-sm text-green-600">Oluşturuldu</p>
                  </div>
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-700">{importDialog.results.updated}</p>
                    <p className="text-sm text-blue-600">Güncellendi</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-700 dark:text-slate-200">{importDialog.results.skipped}</p>
                    <p className="text-sm text-slate-600 dark:text-slate-300">Atlandı</p>
                  </div>
                </div>
                
                {importDialog.results.errors?.length > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="font-medium text-red-700 mb-2">Hatalar:</p>
                    <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
                      {importDialog.results.errors.slice(0, 10).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                      {importDialog.results.errors.length > 10 && (
                        <li>... ve {importDialog.results.errors.length - 10} hata daha</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button onClick={closeImportDialog}>
                  {t('common.close') }
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(v) => !v && closeDeleteDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.deleteUser') || 'Kullanıcıyı Sil'}</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteDialog.user?.full_name}</strong> ({deleteDialog.user?.email})
              <br /><br />
              <span className="text-red-600">
                {t('admin.deleteUserWarning') || 'Bu işlem geri alınamaz. Kullanıcının tüm verileri silinecektir.'}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel') }</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={processing}
              className="bg-red-600 hover:bg-red-700"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.delete') }
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Şub 28, 2026 — Email düzeltme Dialog (SUPER_ADMIN) */}
      <Dialog open={emailDialog.open} onOpenChange={(o) => !o && setEmailDialog({ open: false, user: null, newEmail: '', saving: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-sky-600" />
              Email Adresini Düzelt
            </DialogTitle>
            <DialogDescription>
              <strong>{emailDialog.user?.full_name}</strong> için email adresini düzelt.
              Bu değişiklik login email'ini değiştirecek.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Mevcut email</label>
              <div className="text-sm font-mono bg-slate-50 border rounded px-2 py-1">{emailDialog.user?.email}</div>
            </div>
            <div>
              <label className="text-xs text-slate-500">Yeni email *</label>
              <Input
                type="email"
                value={emailDialog.newEmail}
                onChange={(e) => setEmailDialog({ ...emailDialog, newEmail: e.target.value })}
                data-testid="change-email-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog({ open: false, user: null, newEmail: '', saving: false })} disabled={emailDialog.saving}>
              İptal
            </Button>
            <Button
              onClick={async () => {
                const newEmail = (emailDialog.newEmail || '').trim().toLowerCase();
                if (!newEmail || !newEmail.includes('@')) { toast.error('Geçerli email gir'); return; }
                if (newEmail === emailDialog.user?.email) { toast.error('Email aynı'); return; }
                setEmailDialog({ ...emailDialog, saving: true });
                try {
                  await api.put(`/admin/users/${emailDialog.user.id}/email`, { email: newEmail });
                  toast.success('Email güncellendi');
                  setEmailDialog({ open: false, user: null, newEmail: '', saving: false });
                  fetchUsers();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Güncellenemedi');
                  setEmailDialog({ ...emailDialog, saving: false });
                }
              }}
              disabled={emailDialog.saving}
              data-testid="change-email-save"
            >
              {emailDialog.saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Password Reset Email Dialog */}
      <AlertDialog open={passwordResetDialog.open} onOpenChange={(v) => !v && setPasswordResetDialog({ open: false, user: null, sending: false })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-orange-600" />
              {t('admin.sendPasswordResetTitle') || 'Şifre Sıfırlama Emaili Gönder'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                <strong>{passwordResetDialog.user?.full_name}</strong> kullanıcısına şifre sıfırlama linki içeren bir email gönderilecek.
              </p>
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  <Mail className="inline h-4 w-4 mr-1" />
                  {passwordResetDialog.user?.email}
                </p>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Kullanıcı emaildeki linke tıklayarak yeni şifresini kendisi belirleyebilecek. Link 24 saat geçerlidir.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={passwordResetDialog.sending}>
              {t('common.cancel') }
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSendPasswordReset}
              disabled={passwordResetDialog.sending}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {passwordResetDialog.sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('common.sending') || 'Gönderiliyor...'}
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  {t('admin.sendEmail') || 'Email Gönder'}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Set Password Dialog */}
      <Dialog open={setPasswordDialog.open} onOpenChange={(v) => !v && setSetPasswordDialog({ open: false, user: null, password: '', saving: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-600" />
              {t('admin.setPasswordTitle') || 'Şifre Belirle'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-sm font-medium">{setPasswordDialog.user?.full_name}</p>
              <p className="text-xs text-slate-500">{setPasswordDialog.user?.email}</p>
            </div>
            <div>
              <Label>{t('admin.newPassword') || 'Yeni Şifre'}</Label>
              <Input
                type="text"
                placeholder="En az 6 karakter"
                value={setPasswordDialog.password}
                onChange={(e) => setSetPasswordDialog(prev => ({ ...prev, password: e.target.value }))}
                data-testid="set-password-input"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#';
                let p = '';
                for (let i = 0; i < 10; i++) p += chars.charAt(Math.floor(Math.random() * chars.length));
                setSetPasswordDialog(prev => ({ ...prev, password: p }));
              }}
              data-testid="generate-password-btn"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              {t('admin.generatePassword') || 'Rastgele Oluştur'}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetPasswordDialog({ open: false, user: null, password: '', saving: false })}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={setPasswordDialog.saving || !setPasswordDialog.password || setPasswordDialog.password.length < 6}
              onClick={async () => {
                try {
                  setSetPasswordDialog(prev => ({ ...prev, saving: true }));
                  await api.post(`/admin/users/${setPasswordDialog.user.id}/set-password`, { password: setPasswordDialog.password });
                  toast.success(`${setPasswordDialog.user.full_name} için şifre güncellendi`);
                  setSetPasswordDialog({ open: false, user: null, password: '', saving: false });
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Şifre güncellenemedi');
                  setSetPasswordDialog(prev => ({ ...prev, saving: false }));
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="set-password-confirm-btn"
            >
              {setPasswordDialog.saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
              {t('admin.setPasswordConfirm') || 'Şifreyi Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteDialog.open} onOpenChange={(v) => !v && setBulkDeleteDialog({ open: false })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Toplu Kullanıcı Silme
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                <strong className="text-red-600">{selectedUsers.length} kullanıcı</strong> silinecek. Bu işlem geri alınamaz.
              </p>
              <div className="max-h-[200px] overflow-y-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ad Soyad</TableHead>
                      <TableHead>E-posta</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allUsers.filter(u => selectedUsers.includes(u.id)).map(u => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{u.full_name}</TableCell>
                        <TableCell className="text-slate-500 dark:text-slate-400">{u.email}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Bu kullanıcıların tüm verileri kalıcı olarak silinecektir!
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={processing}
              className="bg-red-600 hover:bg-red-700"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Siliniyor...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {selectedUsers.length} Kullanıcıyı Sil
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create User Dialog - Super Admin Only */}
      <Dialog open={createUserDialog.open} onOpenChange={(v) => !v && closeCreateUserDialog()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-emerald-600" />
              {t('admin.createUser') || 'Yeni Kullanıcı Ekle'}
            </DialogTitle>
            <DialogDescription>
              {t('admin.createUserDesc') || 'Sisteme manuel olarak yeni bir kullanıcı ekleyin'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Sol Kolon */}
            {canFilterFirm && (
              <div className="col-span-2 grid grid-cols-2 gap-4 rounded-lg border border-amber-200 bg-amber-50/40 dark:bg-amber-950/20 p-3">
                <div>
                  <Label>Subco (taşeron)</Label>
                  <select
                    value={createUserDialog.subcoId}
                    onChange={(e) => {
                      const s = subcos.find((x) => x.id === e.target.value);
                      setCreateUserDialog((prev) => ({ ...prev, subcoId: e.target.value, firmId: s?.firm_ids?.length && !s.firm_ids.includes(prev.firmId) ? (s.firm_ids.length === 1 ? s.firm_ids[0] : '') : prev.firmId }));
                    }}
                    className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                    data-testid="create-user-subco-select"
                  >
                    <option value="">— Subco yok (doğrudan firma çalışanı) —</option>
                    {subcos.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  {createSubco && <p className="text-[10px] text-amber-700 mt-1">Firma, {createSubco.name} subco'sunun çalıştığı firmalardan seçilir.</p>}
                </div>
                <div>
                  <Label>Etiketler</Label>
                  <div className="mt-1"><TagPicker tags={userTags} value={createUserDialog.tags || []} onChange={(v) => setCreateUserDialog((prev) => ({ ...prev, tags: v }))} onTagsChanged={setUserTags} /></div>
                </div>
              </div>
            )}
            {/* Firma Seç */}
            <div>
              <Label>{t('common.firm')} *</Label>
              <Select
                value={createUserDialog.firmId}
                onValueChange={(v) => setCreateUserDialog(prev => ({ ...prev, firmId: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('common.selectFirm')} />
                </SelectTrigger>
                <SelectContent>
                  {createFirmOpts.map(firm => (
                    <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* E-posta */}
            <div>
              <Label>{t('admin.email')} *</Label>
              <Input
                className="mt-1"
                type="email"
                placeholder="ornek@firma.com"
                value={createUserDialog.email}
                onChange={(e) => setCreateUserDialog(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            
            {/* Ad Soyad */}
            <div>
              <Label>{t('admin.fullName')} *</Label>
              <Input
                className="mt-1"
                placeholder="Ad Soyad"
                value={createUserDialog.fullName}
                onChange={(e) => setCreateUserDialog(prev => ({ ...prev, fullName: e.target.value }))}
              />
            </div>
            
            {/* Telefon */}
            <div>
              <Label>{t('admin.phone') || 'Telefon'}</Label>
              <Input
                className="mt-1"
                type="tel"
                placeholder="+32 xxx xx xx"
                value={createUserDialog.phone}
                onChange={(e) => setCreateUserDialog(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            
            {/* Dil */}
            <div>
              <Label>{t('common.language') || 'Dil'}</Label>
              <Select
                value={createUserDialog.language}
                onValueChange={(v) => setCreateUserDialog(prev => ({ ...prev, language: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tr">Türkçe</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="nl">Nederlands</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Rol */}
            <div>
              <Label>{t('admin.role') || 'Rol'}</Label>
              <Select
                value={createUserDialog.role}
                onValueChange={(v) => setCreateUserDialog(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EMPLOYEE">Çalışan</SelectItem>
                  <SelectItem value="FIRM_ADMIN">Firma Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Şifre - Full width */}
            <div className="col-span-2">
              <Label>{t('auth.password') || 'Şifre'} *</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="text"
                  placeholder="Şifre belirleyin"
                  value={createUserDialog.password}
                  onChange={(e) => setCreateUserDialog(prev => ({ ...prev, password: e.target.value }))}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generatePassword}
                  className="whitespace-nowrap"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Oluştur
                </Button>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">En az 6 karakter olmalıdır</p>
            </div>
            
            {/* Bilgi Gönder - Full width */}
            <div className="col-span-2 flex items-center space-x-2 pt-1 border-t">
              <Checkbox
                id="sendNotification"
                checked={createUserDialog.sendNotification}
                onCheckedChange={(checked) => setCreateUserDialog(prev => ({ ...prev, sendNotification: checked }))}
              />
              <Label htmlFor="sendNotification" className="text-sm font-normal cursor-pointer">
                <Mail className="h-4 w-4 inline mr-1 text-slate-400" />
                Kullanıcıya giriş bilgilerini e-posta ile gönder
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeCreateUserDialog}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleCreateUser}
              disabled={processing}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Oluşturuluyor...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Kullanıcı Oluştur
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Şub 2026 — Sayfa Yetkisi Lookup */}
      <Dialog open={permLookupOpen} onOpenChange={(v) => { setPermLookupOpen(v); if (!v) setPermLookupData(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
              Yetki Sorgula — Bir sayfaya kim erişebiliyor?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Sayfa yetkisi</Label>
                <select
                  className="w-full h-9 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm"
                  value={permLookupKey}
                  onChange={(e) => setPermLookupKey(e.target.value)}
                  data-testid="perm-lookup-select"
                >
                  {PAGE_KEYS.map((p) => (
                    <option key={p.key} value={p.key}>{p.label} — [{p.key}]</option>
                  ))}
                </select>
              </div>
              <Button
                onClick={async () => {
                  setPermLookupLoading(true);
                  try {
                    const r = await api.get(`/admin/users/permission-lookup?page_key=${encodeURIComponent(permLookupKey)}`);
                    if (r.data?.success) setPermLookupData(r.data.data);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Sorgulanamadı');
                  } finally {
                    setPermLookupLoading(false);
                  }
                }}
                disabled={permLookupLoading}
                data-testid="perm-lookup-run"
              >
                {permLookupLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Sorgula
              </Button>
            </div>

            {permLookupData && (
              <>
                <div className="text-xs text-slate-500">
                  <span className="font-mono px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800">{permLookupData.page_key}</span> yetkisine <b>{(permLookupData.users || []).length}</b> kullanıcı erişebiliyor.
                </div>
                {(permLookupData.users || []).length === 0 ? (
                  <p className="text-xs text-slate-400 py-3 text-center">Kayıt yok.</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-800 text-xs">
                        <tr>
                          <th className="text-left px-3 py-2">Ad</th>
                          <th className="text-left px-3 py-2">Email</th>
                          <th className="text-left px-3 py-2">Rol</th>
                          <th className="text-left px-3 py-2">Erişim</th>
                        </tr>
                      </thead>
                      <tbody>
                        {permLookupData.users.map((u) => (
                          <tr key={u.id} className="border-t" data-testid={`perm-lookup-user-${u.id}`}>
                            <td className="px-3 py-2">{u.full_name || '—'}</td>
                            <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                            <td className="px-3 py-2"><Badge variant="outline">{u.role}</Badge></td>
                            <td className="px-3 py-2">
                              {u.access_reason === 'SUPER_ADMIN' && <Badge className="bg-purple-600 text-white text-[10px]">SUPER_ADMIN</Badge>}
                              {u.access_reason === 'EXPLICIT' && <Badge className="bg-emerald-600 text-white text-[10px]">Yetkilendirilmiş</Badge>}
                              {u.access_reason === 'IMPLICIT (boş yetki listesi)' && <Badge className="bg-amber-500 text-white text-[10px]" title="Yetki listesi boş — geri-uyumluluk gereği tüm sayfalara erişebiliyor">Örtük (yetki listesi boş)</Badge>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermLookupOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
